import { create } from "zustand";

import { api, createActionIdempotencyKey } from "../api/client";

const HELP_LOCK_KEY = "voice-companion.active_help_lock";
const HELP_IDEMPOTENCY_KEY = "voice-companion.active_help_idempotency_key";
const HELP_REQUEST_STATE_KEY = "voice-companion.active_help_request_state";
const HELP_ACTIVE_MESSAGE = "正在呼叫家人，请保持手机畅通。";
const HELP_FAILURE_MESSAGE =
  "网络暂时不可用。求助状态已经锁定，请直接拨打紧急联系人电话。";

export type HelpRequestState = "idle" | "pending" | "unconfirmed" | "sent";

type HelpState = {
  activeHelpLock: boolean;
  requestState: HelpRequestState;
  message: string;
  requestHelp: () => Promise<void>;
  replayHelpStatus: () => void;
  clearLocalLock: () => void;
};

function currentHelpRequestState(): HelpRequestState {
  const existing = localStorage.getItem(HELP_REQUEST_STATE_KEY);
  return existing === "unconfirmed" || existing === "sent" ? existing : "idle";
}

function persistHelpRequestState(requestState: "unconfirmed" | "sent"): void {
  localStorage.setItem(HELP_REQUEST_STATE_KEY, requestState);
}

function currentHelpIdempotencyKey(): string {
  const existing = localStorage.getItem(HELP_IDEMPOTENCY_KEY);
  if (existing) {
    return existing;
  }

  const idempotencyKey = createActionIdempotencyKey("help");
  localStorage.setItem(HELP_IDEMPOTENCY_KEY, idempotencyKey);
  return idempotencyKey;
}

export const useHelpStore = create<HelpState>((set, get) => ({
  activeHelpLock: localStorage.getItem(HELP_LOCK_KEY) === "true",
  requestState: currentHelpRequestState(),
  message: "",
  async requestHelp() {
    const { activeHelpLock, requestState } = get();
    if (activeHelpLock && requestState === "sent") {
      set({ message: HELP_ACTIVE_MESSAGE });
      return;
    }
    if (activeHelpLock && requestState === "pending") {
      set({ message: HELP_ACTIVE_MESSAGE });
      return;
    }

    localStorage.setItem(HELP_LOCK_KEY, "true");
    persistHelpRequestState("unconfirmed");
    const idempotencyKey = currentHelpIdempotencyKey();
    set({
      activeHelpLock: true,
      requestState: "pending",
      message: HELP_ACTIVE_MESSAGE,
    });

    try {
      const result = await api.helpRequest(idempotencyKey);
      persistHelpRequestState("sent");
      set({ requestState: "sent", message: result.message });
    } catch {
      set({
        requestState: "unconfirmed",
        message: HELP_FAILURE_MESSAGE,
      });
    }
  },
  replayHelpStatus() {
    set({ message: HELP_ACTIVE_MESSAGE });
  },
  clearLocalLock() {
    localStorage.removeItem(HELP_LOCK_KEY);
    localStorage.removeItem(HELP_IDEMPOTENCY_KEY);
    localStorage.removeItem(HELP_REQUEST_STATE_KEY);
    set({
      activeHelpLock: false,
      requestState: "idle",
      message: "本机求助锁已经清除。",
    });
  },
}));
